import React, { useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { filterObject } from '@designsystem/stories/01-core/Utilities';
import { BreadcrumbItemProps } from '@designsystem/stories/03-molecules/Breadcrumb';
import { StickyActionsProps } from '@designsystem/stories/04-organisms/StickyActions';
import {
  Intro,
  IntroProps as DSIntroProps,
} from '@designsystem/stories/04-organisms/Intro';
import ContactIntro from './Contact';
import { Editable } from '../Editable';
import { RichText } from '../RichText';
import { normalizeContent } from '@fhnw/helpers/DesignSystem/normalizeContent';
import hyphenate from '@fhnw/helpers/text/hyphenate';
import { ImageSizes } from '@fhnw/helpers/DesignSystem/types';
import { hasBlockType, VoltoBlock } from '@fhnw/helpers/Blocks/traverseBlocks';
import dateFormat from '../DateFormat/dateFormat';

export const introMessages = defineMessages({
  Title: {
    id: 'label_title',
    defaultMessage: 'Title',
  },
  skipToContent: {
    id: 'skip_to_content',
    defaultMessage: 'Skip to content',
  },
  prevPage: {
    id: 'Previous page',
    defaultMessage: 'Previous page',
  },
  nextPage: {
    id: 'Next page',
    defaultMessage: 'Next page',
  },
  swipeToNavigate: {
    id: 'Swipe to navigate',
    defaultMessage: 'Swipe to navigate',
  },
  Registration: {
    id: 'intro_registration',
    defaultMessage: 'Registration',
  },
  InfoEvents: {
    id: 'intro_info_events',
    defaultMessage: 'Info Events',
  },
});

const TYPE_COMPONENTS: {
  [key: string]: React.FC<DSIntroProps & { content?: Content }>;
} = {
  Contact: ContactIntro,
  TeamContact: ContactIntro,
};

const View = ({
  content,
  isEdit,
  isViewOrEdit,
  partial = false,
}: {
  content: Content;
  isEdit?: boolean;
  isViewOrEdit?: boolean;
  partial?: boolean;
}) => {
  const intl = useIntl();
  const location = useLocation();

  const whitelistEntry = (() => {
    if (
      !content?.seo_filter_page ||
      !content?.seo_filter_page_whitelist?.length
    ) {
      return undefined;
    }
    const queryString =
      location.search
        .replaceAll(/(\?|&)(page=\d+|layout=[^&]+|sort_on=[^&]+)/g, '')
        .replace(/^(\?|&)/, '') || '';
    if (!queryString) return undefined;
    return content.seo_filter_page_whitelist.find(
      (entry) => entry.querystring === queryString,
    );
  })();

  const whitelistH1 = whitelistEntry?.h1;
  const whitelistLead = whitelistEntry?.lead;

  const extendedBreadcrumbs = useSelector(
    (state: {
      extendedBreadcrumbs: { items: { title: string; url: string }[] };
    }) => state?.extendedBreadcrumbs?.items,
  );
  const breadcrumb: DSIntroProps['breadcrumb'] = ((extendedBreadcrumbs) => {
    // no breadcrumb on landingpages
    if (content?.['@type'] === 'Landingpage') {
      return undefined;
    }
    const items: BreadcrumbItemProps[] = [];
    extendedBreadcrumbs?.forEach((item) => {
      items.push({
        label: item.title,
        href: item.url,
      });
    });
    items.pop(); // Remove current page from breadcrumb
    if (!items.length) {
      return undefined;
    }
    return {
      primary: items[items.length - 1],
      items: items,
    };
  })(extendedBreadcrumbs);

  const navigationItems = useSelector(
    (state: { content: { data: { items: Content[] } } }) =>
      state?.content?.data?.items,
  );

  const navigation: DSIntroProps['navigation'] = { items: [] };
  if (content?.intro_page_navigation && navigationItems) {
    navigationItems.forEach((item) => {
      if (item?.exclude_from_nav) {
        return;
      }
      navigation?.items?.push({
        label: item.title,
        href: item['@id'],
      });
    });
  }

  // The `lang` attribute in App.jsx comes from content.language, so the page
  // language is what the browser hyphenates against — the default for the title
  // too. A title in a different language overrides it below.
  let titleLanguage =
    (content?.language as { token?: string } | undefined)?.token ?? intl.locale;

  let title: DSIntroProps['title'] = whitelistH1 || content?.title || '';
  if (content?.['@type'] === 'Contact') {
    title = content?.first_name + ' ' + content?.last_name;
    if (content?.name_affix1) {
      title = content?.name_affix1 + ' ' + title;
    }
  } else if (content?.['@type'] === 'TeamContact') {
    title = content?.team_name as string;
  } else if (content?.['@type'] === 'Module') {
    // The language switch picks the title field, and each field carries its own
    // language: BezeichnungD is German, BezeichnungE English — independent of
    // the page. The hyphenation follows the field that is actually rendered, so
    // an English designation on a German page still gets its soft hyphens and a
    // German one on an English page stays untouched. Only when the designation
    // is missing and content.title steps in does the page language apply again.
    const showLang = new URLSearchParams(location.search).get('show_language');
    const designation =
      showLang === 'en' ? content?.BezeichnungE : content?.BezeichnungD;
    title = designation || content?.title || '';
    if (designation) {
      titleLanguage = showLang === 'en' ? 'en' : 'de';
    }
  } else if (isEdit) {
    title = (
      <Editable
        name="title"
        value={title ? (title as string) : ''}
        data={content}
        placeholder={intl.formatMessage(introMessages.Title)}
      />
    );
  }

  // The browser hyphenates `hyphens: auto` text from the OS dictionaries, and
  // macOS ships none for English — so English titles need soft hyphens from us
  // to break like the German and French ones do (#1735). In edit mode the
  // Editable title is a node, not a string, and stays untouched — no soft
  // hyphen may ever reach the stored value.
  if (typeof title === 'string') {
    title = hyphenate(title, titleLanguage);
  }

  let description: DSIntroProps['lead'] = content?.description;
  if (isEdit) {
    description = (
      <Editable
        name="description"
        value={description ? (description as string) : ''}
        data={content}
        placeholder="Einleitungstext (Lead)"
        allowLineBreaks
      />
    );
  } else if (whitelistLead) {
    description = <RichText text={whitelistLead} />;
  } else if (description) {
    description = <RichText text={description as string} />;
  }

  // Compute sticky actions synchronously from content.blocks so SSR and CSR
  // produce the same output, avoiding a layout shift when the sticky bar appears.
  const stickyActions = useMemo<DSIntroProps['stickyActions']>(() => {
    const blockMap = content?.blocks as Record<string, VoltoBlock> | undefined;

    const actions: StickyActionsProps['actions'] = [];

    if (hasBlockType(blockMap, 'infoevent')) {
      actions.push({
        label: intl.formatMessage(introMessages.InfoEvents),
        onClick: (
          e: MouseEvent | React.MouseEvent<HTMLButtonElement, MouseEvent>,
        ) => {
          e.preventDefault();
          window?.document
            ?.querySelector("[data-block-type='infoevent']")
            ?.scrollIntoView({ behavior: 'smooth' });
        },
        type: 'secondary',
        className: 'ga-sticky-infoanlass',
      });
    }

    if (hasBlockType(blockMap, 'application')) {
      actions.push({
        label: intl.formatMessage(introMessages.Registration),
        onClick: (
          e: MouseEvent | React.MouseEvent<HTMLButtonElement, MouseEvent>,
        ) => {
          e.preventDefault();
          window?.document
            ?.querySelector("[data-block-type='application']")
            ?.scrollIntoView({ behavior: 'smooth' });
        },
        type: 'primary',
        className: 'ga-sticky-anmeldung',
      });
    }

    return actions.length > 0 ? { actions } : undefined;
  }, [content?.blocks, intl]);

  if (!isViewOrEdit) {
    if (partial) {
      return null;
    }
    return <Intro title={title} breadcrumb={breadcrumb} />;
  }

  const individualTypeComponent = content?.['@type'] in TYPE_COMPONENTS;
  const Component: React.FC<DSIntroProps & { content?: Content }> =
    TYPE_COMPONENTS[content?.['@type']] || Intro;

  const args: { content?: Content } & Partial<DSIntroProps> = {
    prevLabel: intl.formatMessage(introMessages.prevPage),
    nextLabel: intl.formatMessage(introMessages.nextPage),
    swipeLabel: intl.formatMessage(introMessages.swipeToNavigate),
  };

  if (content?.['@type'] in TYPE_COMPONENTS) {
    args.content = content;
  }

  let divider: DSIntroProps['divider'] = undefined;
  if (content?.intro_divider?.show) {
    divider = normalizeContent(
      filterObject(content?.intro_divider, null, ['show']),
    ) as DSIntroProps['divider'];
  }

  const seoTitleAddition =
    !whitelistEntry?.h1 &&
    content?.['@components']?.university &&
    content['@components'].university?.['@id'] !== content?.['@id']
      ? content['@components'].university?.title
      : undefined;

  if (!divider?.title) {
    let divider_parts: string[] = [];

    if (
      content?.['@components']?.university &&
      content?.['@type'] !== 'University'
    ) {
      divider_parts.push(
        content['@components'].university?.teaser_title ||
          content['@components'].university?.title,
      );
    }

    if (
      content?.['@type'] === 'FHNWNews' &&
      content?.organisation_entities?.length
    ) {
      content.organisation_entities.forEach((entity) => {
        divider_parts.push(normalizeContent(entity, true) as string);
      });
    }

    if (content?.partnership_notice) {
      divider_parts.push(content.partnership_notice);
    }

    if (divider === undefined) {
      divider = {};
    }

    if (divider_parts.length > 0) {
      divider.title = divider_parts.join(', ');
    }

    if (
      content?.['@type'] === 'FHNWNews' &&
      (content.effective || content.created)
    ) {
      if (divider?.title) {
        divider.title = ' – ' + divider.title;
      } else {
        divider.title = '';
      }

      divider.title =
        dateFormat({
          start: new Date((content.effective || content.created) as string),
          dateOnly: true,
        }) + divider.title;
    }

    if (!divider?.title && !content?.intro_divider?.show) {
      divider = undefined;
    }
  }

  let tags: DSIntroProps['tags'] = [];
  if (content?.['@type'] === 'FHNWNews' && content?.news_category) {
    tags.push({
      label: normalizeContent(content.news_category, true) as string,
    });
  }

  const display_type: DSIntroProps['type'] =
    (normalizeContent(content?.intro_display_type) as DSIntroProps['type']) ||
    'regular';
  const image_sizes: Record<string, ImageSizes> = {
    leri: {
      '60rem': '50vw',
    },
  };
  let slides: DSIntroProps['slides'] =
    (normalizeContent(
      content?.intro_slides,
      false,
      image_sizes?.[display_type],
    ) as DSIntroProps['slides']) || [];
  // Unset device flags mean visible (field default).
  const bubbleVisibleOnAnyDevice =
    content?.intro_bubble?.show_desktop !== false ||
    content?.intro_bubble?.show_mobile !== false;
  let teaser: (DSIntroProps['teaser'] & { url?: string }) | undefined =
    (content?.intro_bubble?.show &&
      bubbleVisibleOnAnyDevice &&
      (filterObject(content?.intro_bubble, null, [
        'show',
        'inherit',
      ]) as DSIntroProps['teaser'] & { url?: string })) ||
    undefined;
  if (teaser?.url) {
    teaser.href = teaser.url;
    delete teaser.url;
  }

  if (display_type === 'regular' && !individualTypeComponent) {
    if (partial) {
      if (!slides?.length && !description && !tags?.length) {
        return null;
      }
      // Render description, slides, tags and teaser above content in regular intro
      return (
        <Component
          {...args}
          className="intro--partial"
          type={display_type}
          slides={slides}
          teaser={teaser}
          lead={description}
          tags={tags}
        />
      );
    } else {
      // Remove description, slides, tags and teaser in regular intro
      // Will be rendered above blocks in that case (see above)
      description = undefined;
      slides = undefined;
      tags = undefined;
      teaser = undefined;
    }
  }
  if (display_type === 'full' && !individualTypeComponent) {
    if (partial) {
      if (!description) {
        return null;
      }
      // Render description and tags above content in full intro
      return (
        <Component
          {...args}
          className="intro--partial"
          type={display_type}
          lead={description}
          tags={tags}
        />
      );
    } else {
      // Remove description and tags in full intro
      // Will be rendered above blocks in that case (see above)
      description = undefined;
      tags = undefined;
    }
  } else if (partial) {
    return null;
  }

  return (
    <Component
      {...args}
      type={display_type}
      title={title}
      seoTitleAddition={seoTitleAddition}
      lead={description}
      slides={slides}
      divider={divider}
      actions={
        (normalizeContent(content?.intro_actions) as DSIntroProps['actions']) ||
        []
      }
      breadcrumb={breadcrumb}
      navigation={navigation}
      teaser={teaser}
      skip={
        content?.intro_slides && content?.intro_slides?.length > 0
          ? intl.formatMessage(introMessages.skipToContent)
          : undefined
      }
      stickyActions={stickyActions}
    />
  );
};

export default View;
